// fr landing pages: see config/localeHubs.ts for the rules and
// config/hubs/fr.json for the slug of each page type (keep them in step).
//
// Volumes: OpenSEO, France, monthly searches (Oct 2026). Phrasing from Google
// autocomplete (hl=fr, gl=fr). Most voice-changer suggestions add "gratuit",
// "en direct", "en temps réel", "discord" or "appel": the FAQ answers free and
// real time honestly. Covers are "une cover" (feminine), as French creators
// say it on TikTok and YouTube.
// Skipped on purpose: "voix ia" (5.4k) and "voix ia gratuit" return
// text-to-speech tools, not ours.
import type { HubCategory, HubConfig } from "../hubPages";

const CATEGORIES_FR: HubCategory[] = [
  { label: "Musique", filter: "music" },
  { label: "Anime", filter: "anime" },
  { label: "Dessins animés", filter: "cartoon" },
  { label: "Jeux vidéo", filter: "gaming" },
  { label: "K-pop", filter: "korean" },
  { label: "Célébrités", filter: "public figure" },
];

const FREE_FR = {
  q: "Est-ce gratuit ?",
  a: "Explorer toutes les voix et écouter les covers d'exemple est gratuit. Pour créer des covers, modifier ta voix, cloner une voix ou utiliser la synthèse vocale, il te faut un abonnement. Consulte la page Tarifs pour voir les offres actuelles.",
};

export const HUBS: Record<string, HubConfig> = {
  // "modificateur de voix" 8.1k (KD 0), "changeur de voix" 2.9k,
  // "changeur de voix ia" 110, "transformer sa voix" 90. Autocomplete:
  // "modificateur de voix en ligne", "... ia", "... gratuit", "... en temps
  // réel", "... appel", "transformer sa voix avec ia", "transformer sa voix
  // en femme". Results are online AI changers (Kapwing, voicechanger.im,
  // media.io) and real-time apps: real time answered in the FAQ.
  "modificateur-de-voix": {
    slug: "modificateur-de-voix",
    group: "voice-changer",
    path: "/fr/modificateur-de-voix",
    locale: "fr",
    h1: "Modificateur de voix IA",
    highlight: "voix IA",
    title: "Modificateur de voix IA en ligne : transforme ta voix | Jammable",
    metaDescription:
      "Modificateur et changeur de voix IA en ligne : transforme ta voix en celle d'un personnage, d'un chanteur ou d'une célébrité. Plus de 60 000 voix réalistes.",
    heroSub:
      "Enregistre-toi ou importe un audio, choisis la voix que tu veux avoir et écoute ta voix transformée en quelques secondes.",
    intro:
      "Un modificateur de voix IA ne se contente pas de déformer ta voix avec des effets : il la transforme en une vraie autre voix. Personnages, chanteurs, célébrités ou une voix que tu as entraînée toi-même.",
    appName: "Jammable Modificateur de voix IA",
    steps: [
      {
        title: "Enregistre ou importe",
        body: "Enregistre-toi avec ton micro ou importe l'audio que tu veux transformer.",
      },
      {
        title: "Choisis une voix",
        body: "Plus de 60 000 voix IA, des personnages de dessins animés et de jeux vidéo aux chanteurs et célébrités.",
      },
      {
        title: "Transforme ta voix",
        body: "En quelques secondes, ton audio ressort avec la nouvelle voix, prêt à télécharger.",
      },
    ],
    faqs: [
      {
        q: "C'est quoi un modificateur de voix IA ?",
        a: "C'est un outil qui prend un audio enregistré et le convertit pour qu'il sonne comme une autre voix, grâce à la conversion vocale par IA. Contrairement à un changeur de voix à effets (robot, voix aiguë, écho), le résultat ressemble à la vraie voix choisie.",
      },
      {
        q: "Comment transformer sa voix avec l'IA ?",
        a: "Enregistre-toi ou importe un audio, choisis la voix que tu veux et lance la conversion. Jammable transforme ton audio et te renvoie un résultat propre à télécharger.",
      },
      {
        q: "Ça marche en temps réel, sur Discord ou en appel ?",
        a: "Non. Jammable convertit un audio enregistré ou importé, pas le micro en direct ni les appels. C'est ce qui rend le résultat bien plus réaliste qu'un modificateur de voix en temps réel. Tu peux ensuite télécharger l'audio et l'utiliser où tu veux.",
      },
      {
        q: "Puis-je prendre la voix d'un personnage ?",
        a: "Oui. Il y a des milliers de voix de personnages de dessins animés, d'anime et de jeux vidéo, et de nouvelles voix arrivent chaque jour.",
      },
      FREE_FR,
    ],
    categories: CATEGORIES_FR,
    related: [
      { label: "Cover IA", href: "/fr/cover-ia" },
      { label: "Cloner sa voix avec l'IA", href: "/fr/cloner-voix-ia" },
      { label: "Toutes les voix IA", href: "/models" },
    ],
  },

  // "ai cover" 390, "cover ia" 260, "ia qui chante" 260, "faire chanter ia"
  // 10. Autocomplete: "cover ia gratuit", "cover ia avec ma voix", "faire une
  // cover ia avec sa voix", "ia qui chante un texte", "ia qui chante avec ma
  // voix", "changer la voix d'une chanson avec ia".
  "cover-ia": {
    slug: "cover-ia",
    group: "cover",
    path: "/fr/cover-ia",
    locale: "fr",
    h1: "Cover IA avec n'importe quelle voix",
    highlight: "Cover IA",
    title: "Cover IA : fais chanter n'importe quelle voix | Jammable",
    metaDescription:
      "Fais une cover IA avec plus de 60 000 voix : chanteurs, personnages et célébrités. Une IA qui chante avec la voix de ton choix, en quelques secondes.",
    heroSub:
      "Choisis une voix, ajoute un audio que tu as le droit d'utiliser et Jammable te renvoie une cover IA en quelques secondes.",
    intro:
      "Changer la voix d'une chanson demandait avant un studio et des heures de mixage. Avec Jammable, tu choisis la voix, tu importes ton audio et l'IA fait le reste : elle isole la voix, la convertit et refait le mix.",
    appName: "Jammable Cover IA",
    steps: [
      {
        title: "Choisis une voix",
        body: "Plus de 60 000 voix IA : chanteurs, rappeurs, personnages de dessins animés et d'anime, ou une voix que tu as entraînée.",
      },
      {
        title: "Ajoute ton audio",
        body: "Importe un fichier, enregistre-toi en train de chanter ou écris des paroles pour générer une chanson originale avec l'IA.",
      },
      {
        title: "Crée ta cover",
        body: "Jammable convertit la voix et mixe le résultat. Télécharge-la et partage-la sur TikTok ou YouTube.",
      },
    ],
    faqs: [
      {
        q: "C'est quoi une cover IA ?",
        a: "C'est une version d'un morceau chantée par une autre voix grâce à l'IA. Tu pars d'une piste ou de ta propre voix et tu la convertis en la voix d'un artiste, d'un personnage ou d'une voix que tu as entraînée, en gardant la mélodie et les paroles.",
      },
      {
        q: "Comment changer la voix d'une chanson avec l'IA ?",
        a: "Choisis la voix sur Jammable, importe l'audio et lance la création. Jammable sépare la voix de la musique, la convertit avec la voix choisie et remixe le tout en moins d'une minute. Utilise uniquement des audios que tu as le droit d'utiliser.",
      },
      {
        q: "Comment faire une cover IA avec sa voix ?",
        a: "Entraîne un modèle de ta voix à partir d'au moins 6 minutes d'audio propre, puis utilise-le dans n'importe quelle cover.",
      },
      {
        q: "Existe-t-il une IA qui chante un texte ?",
        a: "Oui. Écris des paroles ou décris une chanson, et la voix choisie la chante ou la dit. Tu peux aussi faire chanter ta propre voix clonée.",
      },
      FREE_FR,
    ],
    categories: CATEGORIES_FR,
    related: [
      { label: "Modificateur de voix IA", href: "/fr/modificateur-de-voix" },
      { label: "Cloner sa voix avec l'IA", href: "/fr/cloner-voix-ia" },
      { label: "Toutes les voix IA", href: "/models" },
    ],
  },

  // "cloner voix" 260, "cloner sa voix ia" 30, "imiter une voix ia" 10.
  // Autocomplete: "cloner voix ia", "cloner sa voix gratuitement", "cloner sa
  // voix pour chanter", "cloner sa voix sur une musique", "cloner une voix à
  // partir d'un audio", "cloner la voix de quelqu'un", "imiter une voix avec
  // ia". Results are ElevenLabs/CapCut/Suno: ours is singing + RVC upload.
  "cloner-voix-ia": {
    slug: "cloner-voix-ia",
    group: "clone",
    path: "/fr/cloner-voix-ia",
    locale: "fr",
    h1: "Cloner sa voix avec l'IA",
    highlight: "voix",
    title: "Cloner sa voix avec l'IA : crée un modèle de ta voix | Jammable",
    metaDescription:
      "Clone ta voix avec l'IA à partir de 6 minutes d'audio, puis fais-la chanter dans tes covers ou parler en synthèse vocale. Importe aussi tes modèles RVC.",
    heroSub:
      "Importe au moins 6 minutes d'audio propre et Jammable entraîne un modèle de cette voix, à utiliser dans tes covers et en synthèse vocale.",
    intro:
      "Cloner une voix avec l'IA, c'est entraîner un modèle qui apprend comment elle sonne. Ensuite, tu peux faire chanter ou parler cette voix comme tu veux. Ne clone que ta propre voix ou des voix que tu as l'autorisation d'utiliser.",
    appName: "Jammable Cloner sa voix",
    steps: [
      {
        title: "Importe ton audio",
        body: "Au moins 6 minutes de voix chantée ou parlée, propre et sans longs silences.",
      },
      {
        title: "Entraîne le modèle",
        body: "Jammable entraîne la voix pour toi. Pas besoin de carte graphique ni de connaissances techniques.",
      },
      {
        title: "Utilise ta voix",
        body: "Fais des covers, convertis des audios ou écris un texte et écoute-le avec ta voix clonée.",
      },
    ],
    faqs: [
      {
        q: "Comment cloner sa voix avec l'IA ?",
        a: "Importe au moins 6 minutes d'audio propre de la voix, donne-lui un nom et Jammable entraîne le modèle. Une fois l'entraînement terminé, la voix apparaît dans ta bibliothèque, prête à l'emploi.",
      },
      {
        q: "Combien d'audio faut-il pour cloner une voix ?",
        a: "Au moins 6 minutes de voix propre, sans musique de fond ni longs silences. Plus l'audio est long et de bonne qualité, plus le résultat est fidèle.",
      },
      {
        q: "Puis-je cloner ma voix pour chanter ?",
        a: "Oui. Une fois ta voix clonée, tu peux la mettre sur n'importe quel audio que tu as le droit d'utiliser : elle chante en gardant la mélodie et les paroles.",
      },
      {
        q: "Puis-je importer un modèle RVC que j'ai déjà ?",
        a: "Oui. Importe les fichiers .pth et .index depuis ta bibliothèque et la voix est prête tout de suite.",
      },
      {
        q: "Peut-on cloner ou imiter la voix de quelqu'un d'autre ?",
        a: "Seulement avec son autorisation. Clone ta propre voix ou des voix que tu as le droit d'utiliser, et n'utilise jamais une voix pour tromper quelqu'un.",
      },
      FREE_FR,
    ],
    categories: CATEGORIES_FR,
    related: [
      { label: "Entraîner ma voix", href: "/train" },
      { label: "Cover IA", href: "/fr/cover-ia" },
      { label: "Modificateur de voix IA", href: "/fr/modificateur-de-voix" },
    ],
  },
};
